/**
 * The intake in the client's language. The page's own copy comes from the
 * portal's strings; the firm's questions come translated from the server and
 * are swapped into a copy of the schema, so every screen reads them. Answers
 * are stored as typed, and option answers are ids, so nothing saved changes.
 */
import { useEffect, useMemo, useState } from "react";

import { PublicOutgoingIntakesAPI } from "@/api/publicOutgoingIntakesApi";
import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import {
  initialPortalLanguage,
  languageDirection,
  portalI18n,
  rememberPortalLanguage
} from "@/features/clientPortal/i18n";

export type IntakeStrings = Record<string, string>;

type Translatable = {
  title: string;
  attorney_note?: string | null;
  schema: OutgoingIntakeFormNode[];
};

const translateNode = (
  node: OutgoingIntakeFormNode,
  strings: IntakeStrings
): OutgoingIntakeFormNode => {
  const id = String(node.node_id ?? "");
  const pick = (key: string, fallback: string | null | undefined) =>
    strings[`${id}.${key}`] ?? fallback;
  return {
    ...node,
    title: pick("title", node.title) ?? node.title,
    prompt: pick("prompt", node.prompt) ?? node.prompt,
    help_text: pick("help", node.help_text) ?? node.help_text,
    options: (node.options ?? []).map((o) => ({
      ...o,
      label: strings[`${id}.opt.${o.option_id}`] ?? o.label
    })),
    children: (node.children ?? []).map((c) => translateNode(c, strings))
  };
};

/** `intake` with its questions in the translation; the same object when
 * there's nothing to apply. */
export const translateIntake = <T extends Translatable>(
  intake: T | null,
  strings: IntakeStrings | null
): T | null => {
  if (!intake || !strings || !Object.keys(strings).length) return intake;
  return {
    ...intake,
    title: strings.title ?? intake.title,
    attorney_note: strings.note ?? intake.attorney_note,
    schema: intake.schema.map((n) => translateNode(n, strings))
  };
};

export type IntakeLanguage = {
  language: string;
  setLanguage: (code: string) => void;
  direction: "rtl" | "ltr";
  /** The questions in English even though the page is translated. */
  showEnglish: boolean;
  setShowEnglish: (on: boolean) => void;
  status: "idle" | "loading" | "failed";
  /** What to apply to the schema (null: English). */
  strings: IntakeStrings | null;
};

export const useIntakeLanguage = ({
  token,
  clientLanguage
}: {
  /** Null in the attorney's preview: no translation call. */
  token: string | null;
  clientLanguage: string | null | undefined;
}): IntakeLanguage => {
  const [chosen, setChosen] = useState<string | null>(null);
  const language = chosen ?? initialPortalLanguage(clientLanguage);
  const [showEnglish, setShowEnglish] = useState(false);
  const [byLanguage, setByLanguage] = useState<Record<string, IntakeStrings>>(
    {}
  );
  const [status, setStatus] = useState<IntakeLanguage["status"]>("idle");

  useEffect(() => {
    if (portalI18n.language !== language) {
      void portalI18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (!token || language === "en" || byLanguage[language]) {
      setStatus("idle");
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");
    PublicOutgoingIntakesAPI.translation(token, language)
      .then((res) => {
        if (cancelled) return;
        setByLanguage((prev) => ({ ...prev, [language]: res.strings }));
        setStatus("idle");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [token, language, byLanguage]);

  const strings = useMemo(
    () =>
      language === "en" || showEnglish ? null : byLanguage[language] ?? null,
    [language, showEnglish, byLanguage]
  );

  return {
    language,
    setLanguage: (code) => {
      rememberPortalLanguage(code);
      setChosen(code);
      setShowEnglish(false);
    },
    direction: languageDirection(language),
    showEnglish,
    setShowEnglish,
    status,
    strings
  };
};
